import React from 'react';
import { styled } from 'linaria/react';
import { css, cx } from 'linaria';
import {
  render,
  NODE_HEADING,
  NODE_IMAGE,
  NODE_QUOTE,
  NODE_PARAGRAPH
} from 'storyblok-rich-text-react-renderer';
import {
  BaseHeading,
  Heading5,
  Quote,
  BaseText
} from '../../../../ui/Typography';
import Image from '../../components/Image';
import { theme } from '../../../../Theme';
import MaxWidth from '../../../../Layout/MaxWidth';
import FeaturedProducts from '../../../../StartPage/Content/FeaturedProducts';
import FeaturedProduct from '../../bloks/FeaturedProduct';
import YotpoGallery from '../../../../ProductPage/YotpoGallery';
import { Below } from '@jetshop/ui/Breakpoints';
import FindifyRecommendationsBlok from '../../bloks/FindifyRecommendations.loadable';
import DynamicComponent from '../../DynamicComponent';
import HeroVideo from '../../components/Video';
import ImageLayout from '../../bloks/ImageLayout';
import ArticleHero from '../../bloks/ArticleHero';
import TextBlock from '../../bloks/TextBlock';
import QuoteBlock from '../../bloks/QuoteBlock';
import VideoPlayer from '../../bloks/VideoPlayer';
import ProductHighlight from '../../bloks/ProductHighlight';
import ShopTheLook from '../../bloks/ShopTheLook';
import ImageAndTextLayout from '../../bloks/TextAndImageLayout';
import JournalCategory from '../../bloks/JournalCategory';
import JournalCategories from '../../bloks/JournalCategories';

const ArticleContainer = css`
  .hero {
    height: 485px;
    margin-bottom: 1.5rem;

    .hidden {
      visibility: hidden;
    }

    ${theme.above.sm} {
      height: 600px;
      margin-bottom: 3.5rem;
    }
  }

  .hero-title {
    ${BaseHeading};
    font-size: 2.25rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    height: 100%;
    text-align: center;
    color: white;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1));

    ${theme.below.sm} {
      font-size: 1.5rem;
    }
  }

  .simple-title {
    ${BaseHeading};
    font-size: 2.25rem;
    text-align: center;
    margin: 2.875rem auto 3.5rem;

    ${theme.below.sm} {
      font-size: 1.5rem;
    }
  }
`;

const heroStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const contentStyles = css`
  ${BaseText};
  margin-bottom: 10rem;

  ${theme.below.md} {
    margin-bottom: 5rem;
  }

  ${theme.below.sm} {
    font-size: 0.875rem;
    margin-bottom: 1.75rem;

    blockquote > p {
      font-size: unset;
    }
  }

  h2 {
    ${Heading5};
    margin-top: 3.5rem;
    margin-bottom: 0.5rem;
    line-height: 1;

    ${theme.below.sm} {
      margin-top: 2rem;
    }
  }

  h2:first-of-type {
    margin-top: 0;
  }

  section > div > h3,
  section > div > h4 {
    font-size: 1rem;
    text-align: center;
    margin-top: 3.5rem;

    ${theme.below.sm} {
      margin-top: 2rem;
    }
  }

  p + p {
    margin-top: 1.5rem;
  }

  p > a {
    color: ${theme.colors.black};
    text-decoration: underline;
  }

  p,
  .narrow {
    width: 100%;
    max-width: 880px;
    margin-left: auto;
    margin-right: auto;
  }

  ${Quote} {
    text-align: center;
    p.narrow {
      max-width: 650px;
      margin: inherit;
    }
    ${theme.below.sm} {
      &.add-margin {
        margin: 2.25rem auto;
      }
    }
  }

  .video-wrapper {
    width: 100%;
    max-width: 880px;
    height: 0;
    margin: 3.5rem auto;
    position: relative;
    padding-bottom: 56.26%;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  .add-margin {
    margin: 2rem auto;
    ${theme.above.sm} {
      margin: 3.5rem auto;
    }
  }

  .featured-image {
    ${theme.below.sm} {
      width: 100vw !important;
      margin-left: -1rem;
    }
  }
`;

const CustomWidth = styled(MaxWidth)`
  max-width: 1200px;
`;

const FullWidthSlider = css`
  ${theme.below.sm} {
    width: calc(100% + 2rem);
    margin-left: -1rem;
  }
`;

const JournalPage = story => {
  if (!story) return null;
  const {
    page_title,
    hero_image,
    journal_categories,
    content: richTextContent,
    body
  } = story;
  const isVideo = hero_image.filename && hero_image.filename.endsWith('.mp4');

  const isBodyEmpty = !body || (Array.isArray(body) && body.length === 0);

  return (
    <article className={ArticleContainer}>
      {isBodyEmpty ? (
        <div>
          {hero_image.filename && (
            <Below breakpoint="sm">
              {matches =>
                isVideo ? (
                  <HeroVideo videoSrc={hero_image.filename} />
                ) : (
                  <div className="hero">
                    <Image
                      fillAvailableSpace={true}
                      src={hero_image.filename}
                      alt={hero_image.alt || page_title}
                      width={matches ? 600 : 1920}
                      imageAspect={matches && '1:1'}
                      className={heroStyles}
                    >
                      <h1
                        className={cx(
                          'hero-title overlay text-shadow',
                          story.hide_title_on_hero && 'hidden'
                        )}
                      >
                        {page_title}
                      </h1>
                    </Image>
                  </div>
                )
              }
            </Below>
          )}
          {journal_categories && (
            <JournalCategories categories={journal_categories} />
          )}
          {richTextContent.content.length > 1 && (
            <CustomWidth>
              <section className={contentStyles}>
                {render(richTextContent, {
                  nodeResolvers: {
                    [NODE_IMAGE]: (children, props) => {
                      const dimensions = props.src.split('/')[5].split('x');
                      const width = dimensions[0];
                      const height = dimensions[1];
                      return (
                        <Image
                          cover={true}
                          aspect={`${width}:${height}`}
                          width={880}
                          className="inline-image narrow add-margin"
                          {...props}
                        />
                      );
                    },
                    [NODE_HEADING]: (children, props) => (
                      <h2 {...props} className="narrow">
                        {children}
                      </h2>
                    ),
                    [NODE_QUOTE]: (children, props) => (
                      <Quote {...props} className="add-margin">
                        {children}
                      </Quote>
                    ),
                    [NODE_PARAGRAPH]: (children, props) => (
                      <p {...props} className="narrow">
                        {children}
                      </p>
                    )
                  },
                  blokResolvers: {
                    image: props => {
                      const dimensions = props.image.filename
                        .split('/')[5]
                        .split('x');
                      const width = dimensions[0];
                      const height = dimensions[1];
                      return (
                        <Image
                          cover={true}
                          className="featured-image add-margin"
                          aspect={`${width}:${height}`}
                          width={1328}
                          src={props.image.filename}
                          alt={props.image.alt}
                        />
                      );
                    },
                    video: props => {
                      const videoUrl = props.embed_url.url;
                      const videoId = videoUrl.includes('youtu.be')
                        ? videoUrl.split('/')[3]
                        : videoUrl.split('/watch?v=')[1];

                      return (
                        <div className="narrow">
                          <div className="video-wrapper add-margin">
                            <iframe
                              width="880"
                              height="495"
                              src={`https://www.youtube.com/embed/${videoId}?rel=0&amp;modestbranding=1&amp;showinfo=0`}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            ></iframe>
                          </div>
                        </div>
                      );
                    },
                    featured_products: props => {
                      return (
                        <div className={FullWidthSlider}>
                          <FeaturedProducts {...props} />
                        </div>
                      );
                    },
                    featured_product: props => {
                      return <FeaturedProduct {...props} />;
                    },
                    yotpo: props => {
                      return <YotpoGallery galleryId={props.galleryId} />;
                    },
                    quote_block: props => {
                      return <QuoteBlock {...props} />;
                    },
                    image_layout: props => {
                      return <ImageLayout {...props} />;
                    },
                    article_hero: props => {
                      return <ArticleHero {...props} />;
                    },
                    text_block: props => {
                      return <TextBlock {...props} />;
                    },
                    video_player: props => {
                      return <VideoPlayer {...props} />;
                    },
                    product_highlight: props => {
                      return <ProductHighlight {...props} />;
                    },
                    shop_the_look: props => {
                      return <ShopTheLook {...props} />;
                    },
                    image_and_text_layout: props => {
                      return <ImageAndTextLayout {...props} />;
                    },
                    journal_category: props => {
                      return <JournalCategory {...props} />;
                    },
                    journal_categories: props => {
                      return <JournalCategories {...props} />;
                    },
                    findify_recommendations: props => (
                      <FindifyRecommendationsBlok
                        recIdString={props.recIdString}
                      />
                    )
                  },
                  defaultBlokResolver: (name, props) => (
                    <p>Missing resolver for "{name}"</p>
                  )
                })}
              </section>
            </CustomWidth>
          )}
        </div>
      ) : (
        <div>{body && <DynamicComponent story={body} />}</div>
      )}
    </article>
  );
};

export default JournalPage;
